import type { de } from './de';
import { jurisdictions } from './en/jurisdictions';

const { federal: _federal, model: _model, ...states } = jurisdictions;

/** English texts, with the same keys as the German ones. */
export const en: typeof de = {
  name: 'Legal texts',
  description:
    'Laws, regulations and technical rules on fire safety, building codes and occupational safety, searchable section by section.',
  nav: { texts: 'Legal texts', sources: 'Manage sources' },
  permissions: {
    norm: {
      read: {
        label: 'Read legal texts',
        description:
          'Search legal texts, open sources and norms. Other modules use it to link the legal basis of defects.',
      },
    },
    source: {
      manage: {
        label: 'Manage legal sources',
        description:
          'Create, change and delete sources, paste full texts, import files and take over the default contents.',
      },
      fetch: {
        label: 'Fetch legal texts from official sources',
        description:
          'Together with "Manage legal sources": create sources from the catalog, "Fetch again" and "Update all", take over new editions and enter own addresses on official portals.',
      },
    },
  },
  roles: { reader: 'Read legal texts', manager: 'Manage legal texts' },
  audit: {
    source: {
      create: 'Legal source created',
      update: 'Legal source changed',
      norms: 'Norms of a legal source changed',
      delete: 'Legal source deleted',
    },
    retrieval: {
      request: 'Legal source fetched again',
      'request-all': 'All legal sources updated',
      accept: 'New edition of a legal source taken over',
      discard: 'Download of a legal source discarded',
      upload: 'File of a legal source uploaded',
    },
  },
  settings: {
    state: {
      label: 'Federal state',
      description:
        'Search and lists show federal law, model codes and the law of this state; with "All states", the law of every state.',
      options: { all: 'All states', ...states },
    },
  },
  jurisdictions,
  kinds: {
    law: 'Act',
    regulation: 'Ordinance',
    'administrative-provision': 'Administrative provision',
    guideline: 'Guideline',
    'technical-rule': 'Technical rule',
    dguv: 'DGUV rules',
    standard: 'Standard (reference only)',
  },
  topics: {
    'fire-safety': 'Fire safety',
    'building-code': 'Building code',
    'site-safety': 'Construction site (SiGeKo)',
    'occupational-safety': 'Occupational safety',
    'building-services': 'Building services',
  },
  guides: {
    texts: {
      title: 'Looking up legal texts',
      summary: 'Search laws and rules, read norms and open the official version.',
    },
    sources: {
      title: 'Maintaining legal sources',
      summary: 'Create sources, split full texts, import files and take over the default contents.',
    },
  },
  texts: {
    title: 'Legal texts',
    description:
      'Laws, regulations and rules of the federation, the model codes and the states, searchable section by section.',
    term: 'Search term',
    termPlaceholder: 'e.g. Brandschutztür selbstschließend or § 35 BauO Bln',
    termHint: 'From two characters on. Word forms count: "Treppenraum" also finds "Treppenräume".',
    state: 'Federal state',
    topic: 'Topic',
    allTopics: 'All topics',
    results_one: '{{count}} hit',
    results_other: '{{count}} hits',
    cited: 'Citation',
    noHits: 'No matching passage found',
    noHitsHint: 'Try other words, reset the topic or choose all states.',
    norms_one: '{{count}} norm',
    norms_other: '{{count}} norms',
    referenceOnly: 'Reference only, wording not stored',
    noText: 'No full text yet',
    empty: 'No legal texts yet',
    emptyHint:
      'The laws and rules of the company show here once the administration has added them.',
    emptyManage:
      'The default contents bring eight laws and rules on fire safety and occupational safety, more come by import.',
    noSources: 'No sources for this selection',
    noSourcesHint: 'Choose another topic or all states.',
  },
  norm: {
    title: 'Norm',
    intro: 'Title and preamble',
    previous: 'Previous',
    next: 'Next',
    edition: 'Edition: {{edition}}',
    official: 'Open official version',
    newTab: '(opens in a new tab)',
    table: 'Table',
    noOfficial: 'No official version is stored for this source.',
    unofficial: 'Unofficial reading version. The official publication is authoritative.',
    notFound: 'This norm no longer exists',
    notFoundHint: 'The source was split again or deleted.',
  },
  source: {
    details: 'Details',
    contents: 'Contents',
    inactive: 'Inactive',
    inactiveTitle: 'This source is inactive',
    inactiveHint:
      'Search and other modules do not find it, only those who maintain sources see it.',
    standardTitle: 'Reference only',
    standardHint:
      'The STAW App does not store the wording of standards such as DIN, VDE or VdS, it is protected by copyright.',
    noText: 'No full text yet',
    noTextHint:
      'Copy the text of the official version and paste it. It is split into sections, numbered sections and annexes.',
    noTextReader: 'The administration of the company can paste the full text.',
    notFound: 'Source not found',
    notFoundHint: 'It was deleted or is no longer active.',
    changed: 'Changed {{when}}',
    notSet: 'Not set',
    open: 'Open',
    pasteText: 'Paste full text',
    delete: 'Delete',
    deleteTitle: 'Delete legal source?',
    deleteDescription:
      '"{{title}}" is deleted with all its norms. Legal bases in other modules stay as text.',
    deleted: 'Legal source deleted.',
    fields: {
      code: 'Short code',
      title: 'Title',
      jurisdiction: 'Jurisdiction',
      kind: 'Kind',
      topics: 'Topics',
      summary: 'Summary',
      edition: 'Edition',
      officialUrl: 'Official version',
      fetchUrl: 'Fetched from',
      active: 'Active',
      norms: 'Norms',
      updated: 'Changed',
    },
  },
  form: {
    createTitle: 'New legal source',
    editTitle: 'Edit legal source',
    codeHint: 'Short form legal bases cite, e.g. "BauO Bln" or "ASR A2.3".',
    summaryHint: 'What it is about, in two or three sentences. Helps when choosing from the list.',
    editionHint: 'e.g. "last amended by the act of 09.07.2026"',
    officialUrlHint: 'Address of the official publication. The norm view links there.',
    fetchUrlHint:
      'HTTPS address of the law on an official portal, e.g. its page on gesetze-im-internet.de, a state law portal or a PDF of the building ministers conference. The wording is fetched after saving.',
    activeHint: 'Neither search nor other modules find inactive sources.',
    created: 'Legal source created.',
    updated: 'Changes saved.',
    reload: 'Reload',
    conflictHint: 'Someone changed the source in the meantime. Reload and enter the change again.',
  },
  text: {
    title: 'Paste full text',
    description:
      'Copy the text of the official version and paste it here. It replaces the current norms of the source.',
    label: 'Full text',
    preview_one: 'Results in {{count}} norm: {{numbers}}',
    preview_other: 'Results in {{count}} norms: {{numbers}}',
    previewEmpty:
      'No norm recognised yet. Sections start with "§ 1", numbered sections of technical rules with "1" or "1.1".',
    tooLong: 'The text is longer than 900,000 characters. Please split it into several sources.',
    conflictHint:
      'Someone changed the source in the meantime. Reload and save again, the pasted text stays.',
    save: 'Split and save',
    saved:
      'Full text saved: {{created}} new, {{updated}} changed, {{removed}} removed, {{unchanged}} unchanged.',
  },
  sources: {
    title: 'Legal sources',
    description: 'Maintain the laws, regulations and rules of the company.',
    new: 'New source',
    search: 'Search sources',
    searchPlaceholder: 'Title or short code',
    status: 'Status',
    active: 'Active',
    allJurisdictions: 'All jurisdictions',
    inactive: 'Inactive',
    empty: 'No legal sources yet',
    emptyHint:
      'Take over the default contents or import a file. Single sources can also be created by hand.',
    noMatches: 'No source found',
    noMatchesHint: 'Change the search term or jurisdiction.',
    defaults: 'Take over default contents',
    defaultsDone_one: '{{count}} legal text taken over.',
    defaultsDone_other: '{{count}} legal texts taken over.',
    defaultsNone: 'All default contents are already there.',
    import: 'Import',
  },
  import: {
    title: 'Import legal sources',
    description:
      'JSON file with sources and their norms, e.g. from BrandDoku. The guide "Maintaining legal sources" describes the format.',
    fileHint:
      'Known sources are recognised by jurisdiction and short code or title and only changed where the file differs. Importing the same file again changes nothing.',
    chooseFile: 'Choose file',
    summary: '"{{file}}": {{sources}} with {{norms}} in total.',
    sourceCount_one: '{{count}} source',
    sourceCount_other: '{{count}} sources',
    normCount_one: '{{count}} norm',
    normCount_other: '{{count}} norms',
    run: 'Import',
    running: 'Importing part {{done}} of {{total}} …',
    done: 'Import finished.',
    close: 'Close',
    sourcesResult: 'Sources',
    normsResult: 'Norms',
    created: 'New',
    updated: 'Changed',
    removed: 'Removed',
    unchanged: 'Unchanged',
    errors: {
      unreadable: 'The file is not valid JSON.',
      format:
        'The file does not have the import format: an object with the list "sources" is expected.',
      empty: 'The file contains no sources.',
      tooLarge:
        'Source {{index}} ("{{title}}") is larger than 900 KB. Please split the full text into several sources.',
      issue: 'Source {{index}} ("{{title}}"): {{reason}}',
      invalid: 'A field does not match the format.',
      failed: 'Import stopped. {{reason}}',
      partial_one: 'The first part has been taken over, a new import continues.',
      partial_other: 'The first {{count}} parts have been taken over, a new import continues.',
    },
  },
  retrieval: {
    title: 'Retrieval from the official source',
    description: 'The wording comes from the portal, nothing is reworded.',
    column: 'Retrieval',
    all: 'Update all',
    allQueued:
      '{{created}} sources created from the catalog, {{queued}} downloads queued, {{skipped}} skipped. The downloads run one after the other in the background.',
    fetch: 'Fetch again',
    queued: 'Download queued.',
    never: 'Never fetched',
    address: 'Address',
    fetchedAt: 'Last fetched',
    state: 'State',
    parserMissing: 'No parser',
    parserMissingHint:
      'The portal offers no full text to fetch (one page per section, only a link with a session ID, or a scan). Copy the full text from there and add it with "Paste full text".',
    uploadOnly: 'Upload file',
    uploadOnlyHint:
      'The state law portal hands the text out in the browser only. Open the regulation there, choose "Aktuelle Gesamtausgabe", download "XML-Ansicht des Dokuments" as .zip and read the file in here with "Upload XML file".',
    portal: 'Open the state law portal',
    more: 'More download actions',
    uploads: {
      xml: 'Upload XML file',
      pdf: 'Upload PDF',
      text: 'Upload full text as text file',
    },
    uploaded: 'File uploaded, it is being read.',
    openDocument: 'Open document in the browser',
    failedTitle: 'Download failed',
    changesTitle: 'Check the new edition',
    created_one: '{{count}} new norm',
    created_other: '{{count}} new norms',
    updated_one: '{{count}} changed norm',
    updated_other: '{{count}} changed norms',
    removed_one: '{{count}} norm removed',
    removed_other: '{{count}} norms removed',
    unchanged_one: '{{count}} norm unchanged.',
    unchanged_other: '{{count}} norms unchanged.',
    unverifiedTitle: 'Not found word for word',
    unverifiedHint:
      'These norms do not appear character for character in the document and are not taken over: {{numbers}}.',
    compare: 'Compare before and after',
    before: 'So far',
    after: 'New',
    new: 'Not there yet',
    accept: 'Take over',
    discard: 'Discard',
    accepted: 'New edition taken over.',
    discarded: 'Download discarded, the norms stay as they were.',
    status: {
      queued: 'Waiting',
      running: 'Fetching',
      ready: 'Check changes',
      unchanged: 'Up to date',
      accepted: 'Taken over',
      discarded: 'Discarded',
      failed: 'Error',
    },
    errors: {
      'invalid-url': 'The address is no HTTPS address on the standard port.',
      'host-not-allowed':
        'The portal is not on the list of official sources, not even after a redirect.',
      'private-address': 'The address leads into an internal network. Refused.',
      'too-many-redirects': 'The portal redirects too often.',
      status: 'The portal did not deliver the document (error status). Check the address.',
      'content-type': 'The portal delivers no XML, HTML or PDF.',
      'too-large': 'The document is larger than allowed.',
      timeout: 'The portal did not answer in time. Fetch again later.',
      network: 'The portal could not be reached. Fetch again later.',
      'unsupported-format': 'The format of the document is not recognised.',
      unreadable: 'The document is damaged or cannot be read.',
      'no-text':
        'The document holds no readable legal text. Many portals show the text only with JavaScript.',
      'no-verified-norms': 'No norm passed the wording check. Nothing taken over.',
      'norm-too-long': 'A norm is too long. Probably headings like "§ 12" are missing.',
      'source-deleted': 'The source has been deleted in the meantime.',
      blocked:
        'The portal refused the automated download (bot protection), which happens there irregularly. Fetch again later or download the document in the browser and upload it via "⋮".',
      unexpected: 'Unexpected error during the download.',
    },
  },
  basis: {
    placeholder: 'e.g. § 35 Abs. 1 BauO Bln; ASR A2.3 Nr. 5',
    search: 'Search norm',
    searchLabel: 'Search a norm for the legal basis',
    searchPlaceholder: 'e.g. Treppenraum Rauchabschluss or § 35 BauO Bln',
    minLength: 'Enter at least two characters.',
    offline: 'The norm search needs a connection. The legal basis can still be typed in as text.',
    loading: 'Searching …',
    empty: 'No norm found.',
    tooLong: 'The legal basis is too long. Remove an entry first.',
    open: '{{citation}}: open wording',
  },
  errors: {
    code: 'Short code: at most 60 characters.',
    title: 'Title is missing or longer than 300 characters.',
    jurisdiction: 'Choose a jurisdiction: federal, model codes or a state.',
    kind: 'Choose a kind.',
    topics: 'Choose topics from the list.',
    summary: 'Summary: at most 400 characters.',
    edition: 'Edition: at most 300 characters.',
    officialUrl: 'Enter a complete address with https://, at most 1000 characters.',
    active: 'Active is yes or no.',
    text: 'The full text is missing, too long or contains no recognisable norm.',
    normTooLong:
      'A norm is longer than 400,000 characters. Check the text: headings such as "§ 12" are probably missing.',
    standardText: 'Standards such as DIN and VDE are references only, their wording is not stored.',
    duplicate: 'A source with this short code or title already exists in this jurisdiction.',
    normNumber: 'The number of a norm is missing or longer than 60 characters.',
    normHeading: 'The heading of a norm is longer than 500 characters.',
    normOutline: 'The outline of a norm is longer than 500 characters.',
    normText: 'The wording of a norm is missing or longer than 400,000 characters.',
    norms: 'At most 5000 norms per source.',
    duplicateNumber: 'A number occurs twice in this source.',
    duplicateSource: 'The source is in the file twice.',
    invalid: 'Check this entry.',
    versionConflict: 'The source has been changed in the meantime.',
    notFound: 'The source no longer exists.',
    forbidden: 'This needs the permission "Manage legal sources".',
    fetchUrl: 'Enter a complete HTTPS address, at most 1000 characters.',
    fetchHost:
      'Only addresses of official portals are allowed (list in the guide "Maintain legal sources").',
    noFetchUrl: 'This source has no address to fetch from.',
    noParser: 'There is no parser for the portal of this source.',
    retrievalRunning: 'This source is being fetched right now.',
    retrievalNotReady: 'This download no longer waits for a decision.',
    uploadOnly:
      'This source cannot be fetched. Download the XML export in the state law portal and upload it.',
    uploadMissing: 'The uploaded file does not belong to this source or is missing.',
  },
};
